import { forwardRef, useState } from 'react';
import { FormGroup, FormLabel } from '@mui/material';
import { Option } from '../types';
import { Checkbox } from '../Checkbox/Checkbox';

/**
 * Props for the CheckboxGroup component.
 *
 * @template T - The type of option values (defaults to string)
 */
export interface CheckboxGroupProps<T = string> {
  /** Array of currently selected values */
  value?: T[];
  /**
   * Callback fired when selections change.
   * Receives the complete updated array of selected values.
   *
   * @param value - The new array of selected values
   */
  onChange?: (value: T[]) => void;
  /**
   * Label for the checkbox group.
   * Required to provide context for the group of checkboxes.
   */
  label: string;
  /**
   * Array of options to display as checkboxes.
   * Each option has a label (displayed text) and value (selected value).
   */
  options: Option<T>[];
  /**
   * Whether all checkboxes in the group are disabled.
   * Individual checkboxes cannot be enabled if the group is disabled.
   */
  disabled?: boolean;
  /**
   * If true, displays a required indicator (* asterisk) next to the label.
   * Note: This is visual only - validation must be handled separately.
   */
  required?: boolean;
  /**
   * Custom equality function to compare option values.
   * Required when using object types for T to properly identify selected items.
   * Defaults to strict equality (===) which works for primitives (string, number, boolean).
   *
   * @param a - First value to compare
   * @param b - Second value to compare
   * @returns true if values are equal, false otherwise
   *
   * @example
   * For object types:
   * ```tsx
   * equalityFn={(a, b) => a.id === b.id}
   * ```
   */
  equalityFn?: (a: T, b: T) => boolean;
  /**
   * Test identifier for automated testing.
   * Applied to the container div.
   */
  'data-testid'?: string;
  /**
   * Data identifier attribute for tracking or identification.
   * Applied to the container div.
   */
  'data-identifier'?: string;
}

/**
 * A checkbox group component for multi-select scenarios.
 * Manages an array of selected values with simple toggle behavior.
 * Supports controlled, uncontrolled, and semi-controlled modes.
 *
 * ## Features
 * - Multi-select with array value management
 * - Controlled, uncontrolled, and semi-controlled modes
 * - Generic Option<T> type for flexible value types (primitives and objects)
 * - Automatic toggle logic (add on check, remove on uncheck)
 * - Customizable equality function for object value types
 * - Disabled state for entire group
 * - Required indicator support
 * - Testing attributes (data-testid, data-identifier)
 *
 * ## Control Modes
 *
 * - **Controlled**: Provide both `value` and `onChange`. Parent manages state.
 * - **Uncontrolled**: Omit both `value` and `onChange`. Component manages internal state.
 * - **Semi-controlled**: Provide `onChange` without `value`. Component manages internal state
 *   but notifies parent of changes (useful for form libraries).
 *
 * ## Usage
 *
 * @example
 * Controlled mode (recommended for most cases):
 * ```tsx
 * const [selectedTags, setSelectedTags] = useState<string[]>([]);
 *
 * <CheckboxGroup
 *   label="Select tags"
 *   options={[
 *     { label: 'React', value: 'react' },
 *     { label: 'TypeScript', value: 'typescript' },
 *     { label: 'Node.js', value: 'nodejs' }
 *   ]}
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 * />
 * ```
 *
 * @example
 * Uncontrolled mode:
 * ```tsx
 * <CheckboxGroup
 *   label="Select tags"
 *   options={[
 *     { label: 'React', value: 'react' },
 *     { label: 'TypeScript', value: 'typescript' },
 *     { label: 'Node.js', value: 'nodejs' }
 *   ]}
 * />
 * ```
 *
 * @example
 * Semi-controlled mode (useful with form libraries):
 * ```tsx
 * <CheckboxGroup
 *   label="Select tags"
 *   options={tagOptions}
 *   onChange={(values) => {
 *     // Notify parent/form library of changes
 *     formik.setFieldValue('tags', values);
 *   }}
 * />
 * ```
 *
 * @example
 * With required indicator:
 * ```tsx
 * <CheckboxGroup
 *   label="Required skills"
 *   options={skills}
 *   value={selectedSkills}
 *   onChange={setSelectedSkills}
 *   required
 * />
 * ```
 *
 * @example
 * Disabled group:
 * ```tsx
 * <CheckboxGroup
 *   label="Unavailable options"
 *   options={options}
 *   value={[]}
 *   disabled
 * />
 * ```
 *
 * @example
 * With numeric values:
 * ```tsx
 * const options: Option<number>[] = [
 *   { label: 'Small (1-10)', value: 1 },
 *   { label: 'Medium (11-50)', value: 2 },
 *   { label: 'Large (51+)', value: 3 }
 * ];
 *
 * <CheckboxGroup
 *   label="Company size"
 *   options={options}
 *   value={selectedSizes}
 *   onChange={setSelectedSizes}
 * />
 * ```
 *
 * @example
 * With testing attributes:
 * ```tsx
 * <CheckboxGroup
 *   label="Preferences"
 *   options={preferences}
 *   value={selected}
 *   onChange={setSelected}
 *   data-testid="preferences-checkbox-group"
 *   data-identifier="user-preferences-field"
 * />
 * ```
 *
 * @example
 * With object values (requires equalityFn):
 * ```tsx
 * interface User {
 *   id: string;
 *   name: string;
 * }
 *
 * const userOptions: Option<User>[] = [
 *   { label: 'Alice', value: { id: '1', name: 'Alice' } },
 *   { label: 'Bob', value: { id: '2', name: 'Bob' } }
 * ];
 *
 * const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
 *
 * <CheckboxGroup
 *   label="Select team members"
 *   options={userOptions}
 *   value={selectedUsers}
 *   onChange={setSelectedUsers}
 *   equalityFn={(a, b) => a.id === b.id}
 * />
 * ```
 */
// Auto-fit columns: 2-up on phone width, 1 column in narrow containers; 6px checkbox
// padding keeps a 36x36 touch target (>= 24x24 a11y minimum)
const formGroupSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  columnGap: 1,
  '& .MuiFormControlLabel-root': { mr: 0 },
  '& .MuiCheckbox-root': { p: '6px' },
};

function CheckboxGroupInner<T = string>(
  {
    value,
    onChange,
    label,
    options,
    disabled,
    required,
    equalityFn = (a, b) => a === b,
    'data-testid': dataTestId,
    'data-identifier': dataIdentifier,
  }: CheckboxGroupProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState<T[] | undefined>(undefined);

  // Determine if component is controlled based on whether value prop is provided
  const isControlled = value !== undefined;

  // Derive effective value: use controlled value if provided, otherwise use internal state.
  // Normalize to array so .some() is safe (e.g. RHF/persisted state may pass non-array).
  const effectiveValue = isControlled ? (Array.isArray(value) ? value : []) : internalValue || [];

  /**
   * Handle checkbox toggle for an option.
   * Adds value to array if unchecked, removes if checked.
   * Uses equalityFn to determine if a value is already selected.
   */
  const handleChange = (optionValue: T, checked: boolean) => {
    const currentValue = effectiveValue;
    const newValue = checked
      ? [...currentValue, optionValue]
      : currentValue.filter((v) => !equalityFn(v, optionValue));

    // Always call onChange if provided
    if (onChange) {
      onChange(newValue);
    }

    // Only update internal state if not controlled
    if (!isControlled) {
      setInternalValue(newValue);
    }
  };

  return (
    <div ref={ref} data-testid={dataTestId} data-identifier={dataIdentifier}>
      <FormLabel required={required}>{label}</FormLabel>
      <FormGroup sx={formGroupSx}>
        {options.map((option, index) => (
          <Checkbox
            key={
              typeof option.value === 'object' && option.value !== null
                ? index
                : String(option.value)
            }
            label={option.label}
            checked={effectiveValue?.some((v) => equalityFn(v, option.value)) || false}
            onChange={(checked) => handleChange(option.value, checked)}
            disabled={disabled}
          />
        ))}
      </FormGroup>
    </div>
  );
}

export const CheckboxGroup = forwardRef(CheckboxGroupInner) as <T = string>(
  props: CheckboxGroupProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;
